import { AnimatePresence, motion } from "framer-motion"
import { Download, Megaphone, Search, Users } from "lucide-react"
import type { ReactNode } from "react"
import type { AudienceMember, AudienceVariable } from "@/entities/audience"
import { fadeIn, fetchingDim } from "@/shared/lib/motion"
import { useShownKey } from "@/shared/lib/use-shown-key"
import { Button } from "@/shared/ui/button"
import { Card } from "@/shared/ui/card"
import { Checkbox } from "@/shared/ui/checkbox"
import { Input } from "@/shared/ui/input"
import { LoadErrorState } from "@/shared/ui/load-error-state"
import { LoadSwap } from "@/shared/ui/load-swap"
import { NativeSelect } from "@/shared/ui/native-select"
import { Skeleton } from "@/shared/ui/skeleton"
import { MemberRow } from "./member-row"

/** Заглушка списка: строка «Отметить всех» и строки людей с именем и подписью. */
export function MembersSkeleton({ withToggle }: { withToggle: boolean }) {
	return (
		<div aria-hidden="true">
			{withToggle ? (
				<div className="mb-2 flex min-h-10 items-center gap-2 px-4">
					<Skeleton className="size-4 rounded" />
					<Skeleton className="h-3.5 w-44 rounded" />
				</div>
			) : null}
			<Card className="overflow-hidden border-border/60">
				<ul className="divide-y divide-border/60">
					{Array.from({ length: 6 }, (_, index) => (
						<li key={index} className="flex items-center gap-3 px-4 py-3">
							{withToggle ? <Skeleton className="size-4 shrink-0 rounded" /> : null}
							<div className="min-w-0 flex-1">
								<div className="flex h-6 items-center gap-2">
									<Skeleton
										className="h-4 rounded"
										style={{ width: `${30 - ((index * 7) % 14)}%` }}
									/>
									<Skeleton className="h-3 w-20 rounded" />
								</div>
								<div className="mt-0.5 flex h-4 items-center">
									<Skeleton className="h-3 w-48 rounded" />
								</div>
							</div>
							<Skeleton className="size-4 shrink-0 rounded" />
						</li>
					))}
				</ul>
			</Card>
		</div>
	)
}

export const AUDIENCE_SORT_OPTIONS = [
	{ value: "active", label: "Недавно писали" },
	{ value: "newest", label: "Сначала новые" },
	{ value: "oldest", label: "Сначала старые" },
	{ value: "name", label: "По имени" },
] as const

/** Список пользователей бота: поиск, фильтры по статусу и ответам, выгрузка CSV. Данные, у вызывающего. */
export function AudienceListSection({
	search,
	onSearchChange,
	variables,
	exporting,
	onExportCsv,
	total,
	filtered,
	isPlaceholderData,
	isLoading,
	isError,
	onRetry,
	members,
	botUsername,
	onResetFilters,
	onOpenMember,
	hasNextPage,
	isFetchingNextPage,
	onFetchNextPage,
	sort,
	onSortChange,
	filters,
	onBroadcast,
	broadcasting = false,
	presets,
	actions,
	saved,
	saveCurrent,
	selectedIds,
	onToggleMember,
	onToggleAll,
	selectionBar,
	listKey = "",
}: {
	search: string
	onSearchChange: (value: string) => void
	variables: AudienceVariable[]
	exporting: boolean
	onExportCsv?: () => void
	total: number
	filtered: boolean
	isPlaceholderData: boolean
	isLoading: boolean
	isError: boolean
	onRetry: () => void
	members: AudienceMember[]
	botUsername: string | undefined
	onResetFilters: () => void
	onOpenMember: (memberId: string) => void
	hasNextPage: boolean | undefined
	isFetchingNextPage: boolean
	onFetchNextPage: () => void
	sort: string
	onSortChange: (value: string) => void
	/** Все фильтры списка плашками. */
	filters?: ReactNode
	/** Рассылка по тем, кто сейчас в списке. */
	onBroadcast?: () => void
	broadcasting?: boolean
	/** Частые выборки в один клик, пока фильтры пусты. */
	presets?: { label: string; onSelect: () => void }[]
	/** Дополнительные действия над теми, кто в списке. */
	actions?: ReactNode
	/** Сохранённые выборки: показываются рядом с быстрыми. */
	saved?: ReactNode
	/** «Сохранить выборку», когда условия выбраны. */
	saveCurrent?: ReactNode
	/** Отмеченные галочками: действия над ними показывает selectionBar. */
	selectedIds?: ReadonlySet<string>
	onToggleMember?: (memberId: string) => void
	/** Отметить или снять всех, кто сейчас на экране. */
	onToggleAll?: () => void
	selectionBar?: ReactNode
	/** Ключ фильтра и порядка: по его смене список сменяется переходом, когда придут данные. */
	listKey?: string
}) {
	const shownKey = useShownKey(listKey, isPlaceholderData)
	const allSelected = members.length > 0 && members.every((member) => selectedIds?.has(member.id))
	return (
		<>
			<div className="space-y-3">
				<div className="flex flex-wrap items-center gap-2">
					<div className="relative min-w-56 flex-1">
						<Search className="pointer-events-none absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
						<Input
							aria-label="Поиск по аудитории"
							placeholder="Имя, @username, ID или ответ"
							className="h-10 rounded-xl pl-9"
							value={search}
							onChange={(event) => onSearchChange(event.target.value)}
						/>
					</div>
					<NativeSelect
						aria-label="Порядок списка"
						className="h-10 w-44 rounded-xl"
						value={sort}
						onChange={(event) => onSortChange(event.target.value)}
					>
						{AUDIENCE_SORT_OPTIONS.map((option) => (
							<option key={option.value} value={option.value}>
								{option.label}
							</option>
						))}
					</NativeSelect>
					{onBroadcast ? (
						<Button
							className="h-10 shrink-0 rounded-xl"
							disabled={broadcasting || total === 0}
							title="Черновик рассылки сразу с этим фильтром"
							onClick={onBroadcast}
						>
							<Megaphone />
							<span className="hidden sm:inline">
								{filtered ? `Рассылка по ним (${total})` : "Рассылка всем"}
							</span>
						</Button>
					) : null}
					{actions}
					{onExportCsv ? (
						<Button
							variant="outline"
							className="h-10 shrink-0 rounded-xl"
							disabled={exporting || total === 0}
							aria-label="Выгрузить всех выбранных в CSV"
							title="CSV содержит всех участников выбранного сегмента, включая следующие страницы"
							onClick={onExportCsv}
						>
							<Download />
							<span className="hidden sm:inline">CSV</span>
						</Button>
					) : null}
				</div>

				<div className="flex flex-wrap items-center gap-2">
					{filters}
					{/* Быстрые выборки и «Сбросить всё» сменяют друг друга перекрёстно. */}
					<AnimatePresence initial={false} mode="popLayout">
						{filtered ? (
							<motion.div
								key="filtered"
								variants={fadeIn}
								initial="hidden"
								animate="visible"
								exit="exit"
								className="flex flex-wrap items-center gap-2"
							>
								{saveCurrent}
								<Button
									type="button"
									variant="ghost"
									className="h-8 rounded-full px-3 text-sm text-muted-foreground"
									onClick={onResetFilters}
								>
									Сбросить всё
								</Button>
							</motion.div>
						) : presets?.length ? (
							<motion.div
								key="presets"
								variants={fadeIn}
								initial="hidden"
								animate="visible"
								exit="exit"
								className="flex flex-wrap items-center gap-2"
							>
								<span className="ml-1 text-sm text-muted-foreground">Быстро:</span>
								{saved}
								{presets.map((preset) => (
									<Button
										key={preset.label}
										type="button"
										variant="ghost"
										className="h-8 rounded-full px-3 text-sm text-muted-foreground hover:text-foreground"
										onClick={preset.onSelect}
									>
										{preset.label}
									</Button>
								))}
							</motion.div>
						) : null}
					</AnimatePresence>
				</div>
			</div>

			<div className={`mt-4 ${fetchingDim(isPlaceholderData)}`}>
				<LoadSwap
					loading={isLoading}
					id={isError ? "error" : members.length ? `list-${shownKey}` : `empty-${filtered}`}
				>
					{isLoading ? (
						<MembersSkeleton withToggle={!!onToggleAll} />
					) : isError ? (
						<LoadErrorState onRetry={onRetry} />
					) : members.length === 0 ? (
						<Card className="border-dashed border-foreground/15 bg-card/40 px-6 py-12 text-center">
							<Users className="mx-auto mb-3 h-8 w-8 text-muted-foreground" />
							{filtered ? (
								<>
									<p className="text-muted-foreground">Никого не нашлось</p>
									<Button variant="ghost" className="mt-3" onClick={onResetFilters}>
										Сбросить фильтры
									</Button>
								</>
							) : (
								<p className="text-muted-foreground">
									Боту пока никто не писал.
									{botUsername ? (
										<>
											{" "}
											Поделитесь ссылкой{" "}
											<a
												href={`https://t.me/${botUsername}`}
												target="_blank"
												rel="noreferrer"
												className="text-primary"
											>
												t.me/{botUsername}
											</a>
										</>
									) : null}
								</p>
							)}
						</Card>
					) : (
						<>
							{onToggleAll ? (
								<div className="mb-2 flex min-h-10 flex-wrap items-center gap-2 px-4">
									<label className="flex cursor-pointer items-center gap-2 text-sm text-muted-foreground">
										<Checkbox
											className="border-muted-foreground/60"
											checked={allSelected}
											onCheckedChange={onToggleAll}
										/>
										{allSelected ? "Снять отметку со всех на экране" : "Отметить всех на экране"}
									</label>
									<AnimatePresence initial={false}>
										{selectionBar ? (
											<motion.div
												key="selection"
												variants={fadeIn}
												initial="hidden"
												animate="visible"
												exit="exit"
												className="flex flex-wrap items-center gap-2"
											>
												{selectionBar}
											</motion.div>
										) : null}
									</AnimatePresence>
								</div>
							) : null}
							<Card className="overflow-hidden border-border/60">
								<ul className="divide-y divide-border/60">
									{members.map((member) => (
										<MemberRow
											key={member.id}
											member={member}
											variables={variables}
											onOpen={() => onOpenMember(member.id)}
											selected={selectedIds?.has(member.id)}
											onToggle={onToggleMember ? () => onToggleMember(member.id) : undefined}
										/>
									))}
								</ul>
							</Card>
							{hasNextPage ? (
								<Button
									variant="ghost"
									className="mt-2 w-full"
									disabled={isFetchingNextPage}
									onClick={onFetchNextPage}
								>
									Показать ещё
								</Button>
							) : null}
						</>
					)}
				</LoadSwap>
			</div>
		</>
	)
}
